import { useEffect, type ReactNode } from "react";
import { MotionConfig } from "framer-motion";
import { useA11yPrefs } from "@/state/useA11yPrefs";

/**
 * Applies the user's accessibility preferences globally:
 *
 *  - **Text size** — sets the root font-size so all rem-based text scales
 *    (Tailwind's text-* sizes are rem), behaving like a built-in zoom (WCAG 1.4.4).
 *  - **Higher contrast** — toggles a `.contrast-more` class on <html>; the token
 *    overrides live in index.css.
 *  - **Reduced motion** — toggles a `.reduce-motion` class (CSS keyframes) AND
 *    forces Framer Motion via `MotionConfig reducedMotion="always"`, which also
 *    makes every `useReducedMotion()` consumer (page transitions, ScrollReveal,
 *    hero stagger) settle instantly. When off we fall back to `"user"` so the OS
 *    `prefers-reduced-motion` setting still wins.
 */
export function A11yProvider({ children }: { children: ReactNode }) {
  const textScale = useA11yPrefs((s) => s.textScale);
  const highContrast = useA11yPrefs((s) => s.highContrast);
  const reduceMotion = useA11yPrefs((s) => s.reduceMotion);

  useEffect(() => {
    // 100% = browser default; clear the inline override so the user's own base
    // font-size preference is respected when our scale is "normal".
    document.documentElement.style.fontSize = textScale === 100 ? "" : `${textScale}%`;
  }, [textScale]);

  useEffect(() => {
    document.documentElement.classList.toggle("contrast-more", highContrast);
  }, [highContrast]);

  useEffect(() => {
    document.documentElement.classList.toggle("reduce-motion", reduceMotion);
  }, [reduceMotion]);

  return (
    <MotionConfig reducedMotion={reduceMotion ? "always" : "user"}>
      {children}
    </MotionConfig>
  );
}
